import { useClient, useWorkspace } from '@playbooks/contexts';
import { Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { Link } from '@playbooks/ui/components/link';
import { Tooltip, TooltipContent, TooltipTrigger } from '@playbooks/ui/components/tooltip';
import { cn } from '@playbooks/ui/lib/utils';

const setupKeys = ['profileComplete', 'connectionComplete', 'merchantComplete', 'followsComplete'];

const AccountMarketingBlock = ({ title, text, icon, href, tone = 'default', target = '', collapsed = false }) => {
	const classNames = {
		root: cn(
			'group/marketing flex h-auto w-full items-center justify-start gap-2.5 rounded-panel border border-border bg-surface p-2.5 text-left hover:border-border-strong hover:bg-surface-card-hover',
			collapsed && 'size-8 justify-center border-transparent bg-transparent p-0',
		),
		content: cn('min-w-0 grow', collapsed && 'hidden'),
		icon: cn(
			'flex-middle size-8 shrink-0 rounded-control border bg-icon-well text-foreground-body',
			tone === 'action' && 'border-action/20 bg-action/10 text-action',
			tone === 'success' && 'border-success/20 bg-success/10 text-success',
		),
	};
	const link = (
		<Link aria-label={title} href={href} target={target} variant='ghost' className={classNames.root}>
			<span className={classNames.content}>
				<span className='block truncate text-xs font-semibold text-foreground'>{title}</span>
				<span className='mt-0.5 block truncate text-[11px] leading-4 text-foreground-subtle'>{text}</span>
			</span>
			<span className={classNames.icon}>
				<Icon {...resolveIcon(icon, 'far')} className='size-3.5' aria-hidden />
			</span>
		</Link>
	);

	return (
		<Tooltip disabled={!collapsed}>
			<TooltipTrigger render={link} />
			<TooltipContent side='right' sideOffset={10}>
				{title}
			</TooltipContent>
		</Tooltip>
	);
};

const AccountMarketingBlocks = ({ target = '', collapsed = false }) => {
	const workspace = useWorkspace();
	const { platformHref } = useClient();
	const account = workspace.workspace || {};
	const subscription = account.subscription || {};
	const isPro = Boolean(account.isPro || subscription.isPro);
	const completedSteps = setupKeys.filter(key => account.setup?.[key]).length;
	const setupComplete = Boolean(account.isVerified || completedSteps === setupKeys.length);
	const remainingSteps = Math.max(setupKeys.length - completedSteps, 0);
	const planBlock = isPro
		? {
				title: "What's new",
				text: 'Latest ships and fixes',
				icon: 'sparkles',
				href: platformHref('/blog/changelog'),
				tone: 'default',
			}
		: {
				title: 'Upgrade to Pro',
				text: 'More credits and seats',
				icon: 'crown',
				href: platformHref('/pricing'),
				tone: 'action',
			};
	const setupBlock = setupComplete
		? {
				title: 'Refer a friend',
				text: 'Earn 100 bonus credits',
				icon: 'gift',
				href: platformHref('/settings/account/referrals'),
				tone: 'success',
			}
		: {
				title: 'Account setup',
				text: `${remainingSteps} of ${setupKeys.length} steps left`,
				icon: 'circle-check',
				href: platformHref('/setup'),
				tone: 'default',
			};

	if (!account.id) return null;

	return (
		<div className={cn('flex-column w-full gap-2', collapsed && 'items-center')}>
			<AccountMarketingBlock {...planBlock} target={target} collapsed={collapsed} />
			<AccountMarketingBlock {...setupBlock} target={target} collapsed={collapsed} />
		</div>
	);
};

export { AccountMarketingBlocks };
